import { useState, useEffect } from 'react'

export interface ConnectionState {
  isOnline: boolean
  isReconnecting: boolean
  lastOnlineTime: number | null
  reconnectAttempts: number
}

export function useConnectionState() {
  const [connectionState, setConnectionState] = useState<ConnectionState>({
    isOnline: navigator.onLine,
    isReconnecting: false,
    lastOnlineTime: navigator.onLine ? Date.now() : null,
    reconnectAttempts: 0
  })

  useEffect(() => {
    let reconnectTimeout: NodeJS.Timeout
    let reconnectInterval: NodeJS.Timeout

    const handleOnline = () => {
      setConnectionState(prev => ({
        ...prev,
        isOnline: true,
        isReconnecting: false,
        lastOnlineTime: Date.now(),
        reconnectAttempts: 0
      }))
      
      // Clear any pending reconnection attempts
      if (reconnectTimeout) clearTimeout(reconnectTimeout)
      if (reconnectInterval) clearInterval(reconnectInterval)
      
      console.log('Connection restored')
    }

    const handleOffline = () => {
      setConnectionState(prev => ({
        ...prev,
        isOnline: false,
        isReconnecting: false,
        reconnectAttempts: 0
      }))
      
      console.log('Connection lost')
    }

    const attemptReconnection = () => {
      setConnectionState(prev => ({
        ...prev,
        isReconnecting: true,
        reconnectAttempts: prev.reconnectAttempts + 1
      }))

      // Simulate reconnection attempt
      reconnectTimeout = setTimeout(() => {
        if (!navigator.onLine) {
          setConnectionState(prev => ({
            ...prev,
            isReconnecting: false
          }))
          
          // Schedule next attempt with exponential backoff
          const delay = Math.min(1000 * Math.pow(2, connectionState.reconnectAttempts), 30000)
          reconnectInterval = setTimeout(attemptReconnection, delay)
        }
      }, 2000)
    }

    // Add event listeners
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Start reconnection attempts if offline
    if (!navigator.onLine) {
      attemptReconnection()
    }

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      
      if (reconnectTimeout) clearTimeout(reconnectTimeout)
      if (reconnectInterval) clearInterval(reconnectInterval)
    }
  }, [connectionState.reconnectAttempts])

  return connectionState
}
